import {useEffect} from 'react';
import {useServiceProviderControl} from '../hooks/useServiceProviderControl';
import {FormField, FormSection, FormActions} from '../../../Presentation/components/common';
import {useFormState, CommonValidationRules, createFieldProps} from '../../../Presentation/utils/formUtils';
import {ServiceProvider} from '../models/serviceProvider.model';


interface UpdateServiceProviderFormProps {
  provider: ServiceProvider;
  onSuccess?: (provider: ServiceProvider) => void;
  onCancel?: () => void;
}

export default function UpdateServiceProviderForm ({
  provider,
  onSuccess,
  onCancel
}: UpdateServiceProviderFormProps) {
  const {updateProvider} = useServiceProviderControl();

  // Centralized form state
  const [formState, formActions] = useFormState(
    {
      username: provider.username || '',
      fullName: provider.fullName || '',
      email: provider.email || '',
      phoneNumber: provider.phoneNumber || '',
      companyName: provider.companyName || '',
      website: provider.website || '',
      description: provider.description || ''
    },
    {
      fullName: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'الاسم الكامل مطلوب';
          }
          if (value.length < 2) {
            return 'الاسم الكامل يجب أن يكون حرفين على الأقل';
          }
          return null;
        }
      },
      email: CommonValidationRules.email,
      phoneNumber: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'رقم الهاتف مطلوب';
          }
          const phoneRegex = /^(\+966|966|0)?5[0-9]{8}$/;
          if (!phoneRegex.test(value.replace(/\s+/g, ''))) {
            return 'صيغة رقم الهاتف غير صحيحة (يجب أن يكون رقم سعودي)';
          }
          return null;
        }
      },
      companyName: CommonValidationRules.required('اسم الشركة'),
      website: {
        customValidator: (value: string) => {
          if (value && !/^https?:\/\/.+/.test(value)) {
            return 'الموقع الإلكتروني يجب أن يبدأ بـ http:// أو https://';
          }
          return null;
        }
      }
    }
  );

  useEffect(() => {
    // Update form data when provider changes
    // Use setFields to update all fields at once to avoid multiple re-renders
    formActions.setFields({
      username: provider.username || '',
      fullName: provider.fullName || '',
      email: provider.email || '',
      phoneNumber: provider.phoneNumber || '',
      companyName: provider.companyName || '',
      website: provider.website || '',
      description: provider.description || ''
    });
  }, [provider.id]); // Only depend on provider.id to avoid infinite loop

  const handleSubmit = async (data: Record<string, string>) => {
    try {
      // Clean and prepare data in camelCase (as ServiceProvider model expects)
      // The Repository layer will convert to snake_case for the API
      const providerData = {
        fullName: data.fullName.trim(),
        email: data.email.toLowerCase().trim(),
        phoneNumber: data.phoneNumber.replace(/\s+/g, ''),
        companyName: data.companyName.trim(),
        website: data.website.trim() || undefined,
        description: data.description.trim() || undefined
      };

      // Send to store/useCase/repository (they expect camelCase)
      const updatedProvider = await updateProvider(provider.id, providerData);

      onSuccess?.(updatedProvider);
    } catch (error) {
      // Error notification is handled by StandardizedErrorHandler in the store
    }
  };

  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); formActions.submit(handleSubmit); }} className="space-y-6">
          <FormSection title="بيانات مزود الخدمة">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Username */}
              <div>
                <FormField
                  type="text"
                  label="اسم المستخدم *"
                  placeholder="أدخل اسم المستخدم"
                  // Username cannot be changed
                  {...createFieldProps('username', formState, formActions)}
                />
                <p className="text-xs text-gray-500 mt-1">اسم المستخدم لا يمكن تغييره</p>
              </div>

              <FormField
                type="text"
                label="الاسم الكامل *"
                placeholder="أدخل الاسم الكامل"
                {...createFieldProps('fullName', formState, formActions)}
              />

              <FormField
                type="email"
                label="البريد الإلكتروني *"
                placeholder="أدخل البريد الإلكتروني"
                {...createFieldProps('email', formState, formActions)}
              />

              <FormField
                type="tel"
                label="رقم الهاتف *"
                placeholder="أدخل رقم الهاتف"
                {...createFieldProps('phoneNumber', formState, formActions)}
              />

              <FormField
                type="text"
                label="اسم الشركة"
                placeholder="أدخل اسم الشركة (اختياري)"
                {...createFieldProps('companyName', formState, formActions)}
              />

              <FormField
                type="text"
                label="الموقع الإلكتروني"
                placeholder="https://example.com"
                {...createFieldProps('website', formState, formActions)}
              />
            </div>

            <FormField
              type="textarea"
              label="الوصف"
              placeholder="أدخل وصف مزود الخدمة"
              rows={3}
              {...createFieldProps('description', formState, formActions)}
            />
          </FormSection>

          <FormActions
            onSubmit={() => formActions.submit(handleSubmit)}
            onCancel={onCancel}
            submitLabel="تحديث مزود الخدمة"
            cancelLabel="إلغاء"
            isLoading={formState.isSubmitting}
            disabled={!formState.isValid}
            showReset={false}
          />
      </form>
    </div>
  );
}
